import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';

interface XPBarProps {
  current: number;
  levelMin: number;
  levelMax: number;
  levelName: string;
}

function getTierColor(name: string): string {
  if (name.startsWith('Expert')) return 'text-accent';
  if (name.startsWith('Advanced')) return 'text-success';
  if (name.startsWith('Intermediate')) return 'text-primary';
  return 'text-muted-foreground';
}

function getTierBg(name: string): string {
  if (name.startsWith('Expert')) return 'bg-accent/10';
  if (name.startsWith('Advanced')) return 'bg-success/10';
  if (name.startsWith('Intermediate')) return 'bg-primary/10';
  return 'bg-muted';
}

export function XPBar({ current, levelMin, levelMax, levelName }: XPBarProps) {
  const progress = levelMax > levelMin
    ? ((current - levelMin) / (levelMax - levelMin)) * 100
    : 100;

  const xpToNext = Math.max(levelMax - current, 0);

  return (
    <div className="w-full space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 text-xs font-display font-bold px-2.5 py-0.5 rounded-full ${getTierBg(levelName)} ${getTierColor(levelName)}`}>
            {levelName}
          </span>
          {xpToNext > 0 && (
            <span className="text-[10px] text-muted-foreground font-body flex items-center gap-0.5">
              <ChevronRight className="w-3 h-3" />
              {xpToNext} XP to next level
            </span>
          )}
        </div>
        <span className="display-number text-sm text-primary">
          {current.toLocaleString()} XP
        </span>
      </div>

      {/* Progress tiers visualization */}
      <div className="relative">
        <div className="w-full h-3 bg-muted rounded-full overflow-hidden border border-border">
          <motion.div
            className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(progress, 100)}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Tier progression breadcrumb */}
      <div className="flex items-center justify-center gap-1 text-[10px] font-display">
        {['Beginner', 'Intermediate', 'Advanced', 'Expert'].map((tier, i) => {
          const isActive = levelName.startsWith(tier);
          const isPast = (
            (tier === 'Beginner' && !levelName.startsWith('Beginner')) ||
            (tier === 'Intermediate' && (levelName.startsWith('Advanced') || levelName.startsWith('Expert'))) ||
            (tier === 'Advanced' && levelName.startsWith('Expert'))
          );
          return (
            <span key={tier} className="flex items-center gap-1">
              {i > 0 && <span className="text-border">→</span>}
              <span className={`font-bold ${
                isActive ? 'text-primary' : isPast ? 'text-success' : 'text-muted-foreground/40'
              }`}>
                {tier}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}
